import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
} from "react";
import { loginUser } from "../api/authApi";

const AuthContext = createContext({
  authData: null,
  isAuthenticated: false,
  userId: null,
  token: null,
  handleLogin: () =>
    Promise.resolve({
      status: "error",
      message: "AuthProvider not initialized",
    }),
  handleLogout: () => {},
  remainingDays: 0,
});

export const AuthProvider = ({ children }) => {
  const [authData, setAuthData] = useState(null);

  // ✅ Restore from localStorage on mount
  useEffect(() => {
    const storedAuth = localStorage.getItem("authData");
    if (storedAuth) {
      setAuthData(JSON.parse(storedAuth));
    }
  }, []);

  const handleLogin = async ({ username, password }) => {
    try {
      const response = await loginUser({ username, password });

      if (response?.status === "success") {
        const minimalAuthData = {
          token: response.token,
          userId: response.data?.id,
          remaining_days: response.remaining_days,
        };

        // ✅ Save to context + localStorage
        setAuthData(minimalAuthData);
        localStorage.setItem("authData", JSON.stringify(minimalAuthData));

        return response;
      }

      return {
        status: "error",
        message: response?.message || "Login failed",
      };
    } catch (error) {
      console.error("Login error:", error);
      return {
        status: "error",
        message: error.response?.data?.message || "Network error occurred",
      };
    }
  };

  const handleLogout = () => {
    setAuthData(null);
    localStorage.removeItem("authData"); // ✅ clear storage
  };

  const value = useMemo(
    () => ({
      authData,
      isAuthenticated: !!authData?.token,
      userId: authData?.userId,
      token: authData?.token,
      handleLogin,
      handleLogout,
      remainingDays: authData?.remaining_days || 0,
    }),
    [authData]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
